/**
 * Core API utilities - shared by all domain modules
 * Exported separately to avoid circular imports
 */

// Determine API base URL – env wins so you can always point to deployed (Railway)
function getApiBase() {
  const deployed = process.env.REACT_APP_RAILWAY_URL || process.env.REACT_APP_API_URL;
  if (deployed) return deployed;

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'yobitch.fun' || hostname === 'www.yobitch.fun' || hostname === 'lab.yobitch.fun') {
      return 'https://api.yobitch.fun';
    }
    if (hostname.includes('vercel.app')) {
      return 'https://api.yobitch.fun';
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
  }
  return 'http://localhost:3001';
}

export const API_BASE = getApiBase();

export class ApiError extends Error {
  constructor(message, category, status) {
    super(message);
    this.name = 'ApiError';
    this.category = category;
    this.status = status;
  }
}

// Shared request function for domain modules
const unavailableEndpoints = new Map();

// Critical endpoints that should never be cached as unavailable
const NEVER_CACHE_ENDPOINTS = ['/api/solana/balance', '/api/config/status'];

export async function request(endpoint, options = {}) {
  // Skip cache for critical endpoints
  const isCritical = NEVER_CACHE_ENDPOINTS.some(e => endpoint.startsWith(e));
  
  if (!isCritical) {
    const cacheEntry = unavailableEndpoints.get(endpoint);
    if (cacheEntry) {
      const timeSinceError = Date.now() - cacheEntry.timestamp;
      const cacheDuration = cacheEntry.status === 429 ? 60000 : 30000;
      if (timeSinceError < cacheDuration) {
        return {
          error: cacheEntry.status === 429 ? 'Rate limited' : 'Service unavailable',
          status: cacheEntry.status,
          authenticated: false,
        };
      }
    }
  }

  const url = `${API_BASE}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (response.status === 503 || response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      
      // Don't cache critical endpoints
      if (!isCritical) {
        const cacheDuration =
          response.status === 429 ? (retryAfter ? parseInt(retryAfter) * 1000 : 60000) : 30000;

        unavailableEndpoints.set(endpoint, {
          status: response.status,
          timestamp: Date.now(),
        });

        setTimeout(() => {
          unavailableEndpoints.delete(endpoint);
        }, cacheDuration);
      }

      return {
        error: response.status === 429 ? 'Rate limited' : 'Service unavailable',
        status: response.status,
        authenticated: false,
        retryAfter: retryAfter ? parseInt(retryAfter) : null,
      };
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      if (!response.ok) {
        throw new ApiError('Invalid response', 'PARSE_ERROR', response.status);
      }
      throw new ApiError('Failed to parse response', 'PARSE_ERROR', response.status);
    }

    if (!response.ok) {
      throw new ApiError(data.error || 'Request failed', data.category, response.status);
    }

    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new ApiError('Request timed out. Please check your connection.', 'TIMEOUT_ERROR', 0);
    }
    if (error instanceof ApiError) throw error;
    if (error.message?.includes('503') || error.message?.includes('Service Unavailable')) {
      return { error: 'Service unavailable', status: 503 };
    }
    throw new ApiError('Backend not available', 'NETWORK_ERROR', 0);
  }
}
